import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { skipToken } from '@apollo/client/react';
import { Col, Dialog, Text } from '@opendoor/bricks-next';

import { useMutation, useQuery } from 'platform/apollo';
import { useAuth } from 'platform/auth-fe';
import { Link } from 'platform/bricks-next';
import {
  CustomerTosStatusDocument,
  SetCustomerTosAcceptedDocument,
} from 'platform/graphql/__generated__/TOSStatus';
import { useObservability } from 'platform/observability';
import { ClientOnly } from 'platform/remix-run/utils/client-only';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';

/**
 * Hook to retrieve data and functions to use with the TOS Modal
 *
 * @returns TOSModalContext data
 */
export const useTOSContext = () => useContext(TOSContext);

type TOSContext = {
  /**
   * Gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading,error when it should never be shown
   */
  forceHideTOSModal: (hideModal: boolean) => void;
  /**
   * Determines if the TOS modal should be shown
   */
  showTOSModal: boolean;
  /**
   * adds a loading state to the customer TOS button on acceptance
   */
  isTOSAcceptanceLoading: boolean;
  /**
   * Called when the customer accepts the TOS and updates the status
   */
  customerAcceptsTOS: () => void;
};

const TOSContext = createContext({
  forceHideTOSModal: (_hideModal: boolean) => {},
  showTOSModal: false,
  isTOSAcceptanceLoading: false,
  customerAcceptsTOS: () => {},
});

export const TOSProvider: FC<PropsWithChildren> = ({ children }) => {
  const { trackError } = useObservability();
  const { state, user } = useAuth();
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [isTOSAcceptanceLoading, setCustomerAcceptedLoading] = useState(false);
  const [tosModalOverriden, setTOSModalOverriden] = useState(false);

  /**
   * Determines if the Customer should see the TOS modal
   * Skips checking the TOSStatusEndpoint if it's a webview or use is not authenticated
   * We store the result in state - tosUpdateRequired and update the state if customer accepts TOS - so we don't show the TOS modal again while the backend updates the status
   */
  const [tosUpdateRequired, settosUpdateRequired] = useState(false);

  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  const { data, loading } = useQuery(
    CustomerTosStatusDocument,
    state !== 'authenticated' || isOpendoorAppEvoWebView || user?.impersonated
      ? skipToken
      : {
          context: { linkContext: { team: 'seller' } },
        },
  );

  const updateRequired = !loading && !!data?.customerTos.getTOSStatus?.updateRequired;
  useEffect(() => {
    settosUpdateRequired(updateRequired);
  }, [updateRequired]);

  useEffect(() => {
    if (tosUpdateRequired && !tosModalOverriden) {
      setShowTOSModal(true);
    }

    return () => setShowTOSModal(false);
  }, [tosUpdateRequired, tosModalOverriden]);

  /**
   * This function gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading or error when it should never be shown
   */
  const forceHideTOSModal = useCallback(
    (hideModal: boolean) => {
      if (hideModal) {
        setShowTOSModal(!hideModal);
        // this prevents us from pre-maturely flashing the modal on the loading page
        setTOSModalOverriden(true);
      } else {
        setShowTOSModal(tosUpdateRequired);
        setTOSModalOverriden(false);
      }
    },
    [tosUpdateRequired],
  );

  /**
   * Customer accepts the TOS and updates the status
   */
  const [setCustomerTOSAccepted] = useMutation(SetCustomerTosAcceptedDocument, {
    context: { linkContext: { team: 'seller' } },
  });

  const customerAcceptsTOS = useCallback(() => {
    // ensures a customer can't click the button multiple times so we're setting the loading state to true
    setCustomerAcceptedLoading(true);
    // tracks an error if the mutation takes more than 10 seconds
    const timeoutId = setTimeout(() => {
      trackError(
        'set customer TOS accepted error',
        new Error('setCustomerTOSAccepted mutation took more than 10 seconds'),
      );
    }, 10000);

    setCustomerTOSAccepted()
      .then(() => {
        setShowTOSModal(false);
        settosUpdateRequired(false);
      })
      .catch((error) => {
        trackError('set customer TOS accepted error', error);
      })
      .finally(() => {
        setCustomerAcceptedLoading(false);
        // if setCustomerTOSAccepted is successful or fails before the timeout clear the timeout
        clearTimeout(timeoutId);
      });
  }, [setCustomerTOSAccepted, trackError]);

  return (
    <TOSContext.Provider
      value={{
        forceHideTOSModal,
        showTOSModal,
        isTOSAcceptanceLoading,
        customerAcceptsTOS,
      }}
    >
      {children}
    </TOSContext.Provider>
  );
};

export const TOSModal: FC = () => {
  const { showTOSModal, customerAcceptsTOS, isTOSAcceptanceLoading } = useTOSContext();

  return (
    <ClientOnly>
      {() => (
        <Dialog
          analyticsName="TOS-Modal"
          isOpen={showTOSModal}
          setIsOpen={() => {}}
          dismissable={false}
          showCloseButton={false}
        >
          <Dialog.Content title="We’ve updated our terms" description="" hideDescription>
            {/* this Col's maxWidth is slightly larger than the title's length */}
            <Col gap="$6x" $largerThanSM={{ gap: '$8x', maxWidth: '$548x' }}>
              <Text typography="$bodyMedium" tag="p">
                We encourage you to review our updated{' '}
                <Link
                  aria-label="open Opendoor’s terms of service in a new window"
                  href="https://www.opendoor.com/terms"
                  display="inline"
                  target="_blank"
                  analyticsName="tos-modal-link"
                  data-testid="tos-link"
                  whiteSpace="nowrap"
                  variant="secondary"
                  trackImpression
                >
                  Terms of Service
                </Link>
                . These Terms contain an arbitration agreement under which both you and Opendoor
                agree to resolve disputes through final and binding arbitration on an individual
                basis, and not by way of a lawsuit in court or a class action of any kind.
              </Text>
              <Text typography="$bodyMedium" tag="p">
                By continuing, you agree to the updated Terms.
              </Text>
            </Col>
          </Dialog.Content>
          <Dialog.Actions
            buttonOneProps={{
              onPress: customerAcceptsTOS,
              'aria-label': 'Accept the Terms and Conditions',
              analyticsName: 'accept-tos',
              loading: isTOSAcceptanceLoading,
              label: 'Continue',
              size: 'small',
            }}
          />
        </Dialog>
      )}
    </ClientOnly>
  );
};
